import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import React from "react";
import { css } from "styled-components";

export interface OrderLine {
    isActiveProduct?: boolean;
    isDiscontinued?: boolean;
    properties?: {
        isNotInCurrentCatalog?: string;
    };
}

export interface OrderData {
    orderLines?: OrderLine[] | null;
}

export interface ProblematicItemsCount {
    inactiveCount: number;
    discontinuedCount: number;
    notInCatalogCount: number;
}

export interface ReorderWarningModalStyles {
    warningModal?: ModalPresentationProps;
    warningModalContainer?: GridContainerProps;
    warningModalTitle?: TypographyProps;
    warningModalMessage?: TypographyProps;
    warningModalButtonContainer?: GridItemProps;
    warningModalCancelButton?: ButtonPresentationProps;
    warningModalProceedButton?: ButtonPresentationProps;
}

export interface ReorderWarningModalProps {
    isOpen: boolean;
    message: string | React.ReactNode;
    onClose: () => void;
    onProceed: () => void;
    extendedStyles?: ReorderWarningModalStyles;
}

export const reorderWarningModalStyles: ReorderWarningModalStyles = {
    warningModal: {
        size: 944,
        cssOverrides: {
            modalTitle: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 18px 15px;
                            `,
                            null,
                            css`
                                padding: 28px 33px;
                            `,
                        ],
                        "min",
                    );
                }}
                [class^="TypographyStyle"] {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                    line-height: 25px;
                                `,
                                null,
                                css`
                                    font-size: 20px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
            `,
            modalContent: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 24px 18px;
                            `,

                            css`
                                padding: 17px 30px;
                            `,
                        ],
                        "min",
                    );
                }}
            `,
            modalContainer: css`
                width: 100%;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                                height: auto;
                            `,
                            null,
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                            `,
                        ],
                        "min",
                    );
                }}
            `,
            scrim: css`
                display: flex;
                align-items: center;
            `,
        },
    },
    warningModalContainer: {
        gap: 20,
    },
    warningModalTitle: {
        variant: "h3",
        weight: 700,
        css: css`
            margin-bottom: 16px;
        `,
    },
    warningModalMessage: {
        variant: "p",
        css: css`
            line-height: 2.5;
            text-align: center;
        `,
    },
    warningModalButtonContainer: {
        width: 12,
        css: css`
            display: flex;
            gap: 12px;
            justify-content: center;
        `,
    },
    warningModalCancelButton: {
        variant: "secondary",
    },
    warningModalProceedButton: {
        variant: "primary",
    },
};

export const hasNonReorderableItems = (orderData: OrderData | null | undefined): boolean => {
    if (!orderData?.orderLines) {
        return false;
    }
    const lines = orderData.orderLines;
    if (!lines) {
        return false;
    }
    return lines.some((line: OrderLine) => {
        const isInactive = line.isActiveProduct === false;
        const isDiscontinuedProduct = line.isDiscontinued === true;
        const isNotInCurrentCatalog = line.properties?.isNotInCurrentCatalog?.toLowerCase() === "true";
        return isInactive || isDiscontinuedProduct || isNotInCurrentCatalog;
    });
};

export const getProblematicItemsCount = (orderData: OrderData | null | undefined): ProblematicItemsCount => {
    let inactiveCount = 0;
    let discontinuedCount = 0;
    let notInCatalogCount = 0;

    if (orderData?.orderLines) {
        const lines = orderData.orderLines;
        if (lines) {
            lines.forEach((line: OrderLine) => {
                if (line.properties?.isNotInCurrentCatalog?.toLowerCase() === "true") {
                    notInCatalogCount++;
                } else if (line.isActiveProduct === false) {
                    inactiveCount++;
                }
                if (line.isDiscontinued === true) {
                    discontinuedCount++;
                }
            });
        }
    }
    return { inactiveCount, discontinuedCount, notInCatalogCount };
};

export const getReorderWarningMessage = (orderData: OrderData | null | undefined): string | React.ReactNode | null => {
    if (!orderData) {
        return null;
    }

    const { inactiveCount, discontinuedCount, notInCatalogCount } = getProblematicItemsCount(orderData);
    const nonReorderableItems = inactiveCount + notInCatalogCount;
    const totalItems = orderData?.orderLines?.length || 0;
    const goodItems = totalItems - nonReorderableItems;

    const formatItemMessage = (key: string, count: number) =>
        (siteMessage(key) as string).replace("{0}", `${count} item${count > 1 ? "s are" : " is"}`);

    const unavailableItemsText = (count: number) => `${count === 1 ? "the 1 item is" : `all ${count} items are`}`;

    const formatOrderableItemMessage = (key: string, count: number) =>
        (siteMessage(key) as string).replace("{0}", `${count} item${count > 1 ? "s" : ""}`);

    const messages = [];
    if (inactiveCount > 0) {
        messages.push(formatItemMessage("item-not-available-online", inactiveCount));
    }
    if (discontinuedCount > 0) {
        messages.push(formatItemMessage("item-discontinued", discontinuedCount));
    }
    if (notInCatalogCount > 0) {
        messages.push(formatItemMessage("item-not-in-catalog", notInCatalogCount));
    }

    if (goodItems === 0) {
        return (
            <>
                {(siteMessage("cannot-reorder") as string).replace("{0}", unavailableItemsText(totalItems))}
                {messages.map((message, index) => (
                    <p key={index}>{message}.</p>
                ))}
            </>
        );
    }
    return (
        <>
            {messages.map((message, index) => (
                <p key={index}>{message}.</p>
            ))}
            {formatOrderableItemMessage("item-can-still-be-reordered", goodItems)}
        </>
    );
};

const ReorderWarningModal: React.FC<ReorderWarningModalProps> = ({
    isOpen,
    message,
    onClose,
    onProceed,
    extendedStyles = {},
}) => {
    const styles = { ...reorderWarningModalStyles, ...extendedStyles };
    const modalTitle = translate("Reorder Warning");
    const cancelButtonText = translate("Cancel");
    const proceedButtonText = translate("Proceed with Re-order");
    return (
        <Modal {...styles.warningModal} isOpen={isOpen} headline={modalTitle} handleClose={onClose}>
            <GridContainer {...styles.warningModalContainer}>
                <GridItem {...styles.warningModalButtonContainer}>
                    <Typography {...styles.warningModalMessage}>{message}</Typography>
                </GridItem>
                <GridItem {...styles.warningModalButtonContainer}>
                    <Button {...styles.warningModalCancelButton} onClick={onClose}>
                        {translate(cancelButtonText)}
                    </Button>
                    <Button {...styles.warningModalProceedButton} onClick={onProceed}>
                        {translate(proceedButtonText)}
                    </Button>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default ReorderWarningModal;
